import { queryOptions, useQuery } from "@tanstack/react-query";
import { api, currentCompany, type S } from "@/shared/api/client";
import { followLanguage } from "@/shared/i18n";
import { setTimeZone } from "./format";

export type Me = S["Me"];

export const meQuery = queryOptions({
  queryKey: ["me"],
  queryFn: async () => {
    const me = await api.get<Me>("/v1/me");
    if (me.current_company_id && currentCompany.get() !== me.current_company_id) currentCompany.set(me.current_company_id);
    setTimeZone(me.companies?.find((c) => c.id === me.current_company_id)?.timezone);
    followLanguage(me.locale);
    return me;
  },
  staleTime: 60_000,
  retry: false,
});

export function useMe() {
  return useQuery(meQuery);
}

/** Returns a checker for permissions in the current company (UI gating only; the API enforces). */
export function useCan() {
  const { data } = useMe();
  return (perm: string) => !!data?.permissions?.[perm];
}

export function useModule() {
  const { data } = useMe();
  return (m: string) => !!data?.tenant.modules?.includes(m);
}

/** Base currency of the current company. */
export function useBaseCurrency(): string {
  const { data } = useMe();
  return data?.companies?.find((c) => c.id === data.current_company_id)?.base_currency ?? "EGP";
}

/** Country of the current company: its tax rules follow it. */
export function useCountry(): string {
  const { data } = useMe();
  return data?.companies?.find((c) => c.id === data.current_company_id)?.country ?? "";
}

/** The tax returns a company in the country files: VAT (Kuwait and Qatar have none yet), and withholding (خصم وإضافة) in Egypt. */
export function taxReturns(country: string): { vat: boolean; wht: boolean } {
  return { vat: !["KW", "QA"].includes(country), wht: country === "EG" };
}

export function useTaxReturns() {
  return taxReturns(useCountry());
}

/** The currencies the current company deals in: its base currency, then the others chosen in company settings. */
export function useCurrencies(): string[] {
  const { data } = useMe();
  const c = data?.companies?.find((x) => x.id === data.current_company_id);
  return [c?.base_currency ?? "EGP", ...(c?.currencies ?? [])];
}
